/**
 * @fileoverview The one-minute lock between two phrase draws.
 * @module lib/domain/cooldown
 *
 * @description
 * A phrase you can reroll ten times a second is a phrase you never read. The
 * cooldown is the product: one line, one minute, take it or leave it.
 *
 * Everything here is a pure function of `drawnAt` and `now`, which is what
 * makes the lock survive a reload — the deadline is derived from a stored
 * instant, never from a timer that dies with the page.
 */

import type { Cooldown } from "@/types/types"

/** How long the draw stays locked after a phrase is pulled, in ms. */
export const COOLDOWN_MS = 60_000

/** A cooldown that is over, or never started. */
const READY: Cooldown = { isReady: true, remainingMs: 0, ratio: 1 }

/**
 * Where the cooldown stands at a given instant.
 *
 * @description
 * Unparseable values and clocks that moved by more than the cooldown itself
 * resolve to "ready": a corrupt stored value must never leave the user
 * staring at a lock that outlives the cooldown.
 *
 * A *small* negative elapsed is a different animal and is clamped to zero
 * rather than unlocked — callers read the clock on a coarse grid, so the
 * render that starts a lock routinely sees a `now` a fraction of a second
 * behind the instant it was just handed.
 *
 * @param drawnAt - ISO-8601 instant of the last draw, or null if never.
 * @param now - Current epoch milliseconds.
 * @param cooldownMs - Lock duration. Injectable for tests.
 * @returns Readiness, milliseconds left, and elapsed share.
 *
 * @example
 * computeCooldown("2026-09-19T10:00:00.000Z", Date.parse("2026-09-19T10:00:45Z"))
 * // => { isReady: false, remainingMs: 15000, ratio: 0.75 }
 */
export function computeCooldown(
	drawnAt: string | null,
	now: number,
	cooldownMs: number = COOLDOWN_MS
): Cooldown {
	if (drawnAt === null || cooldownMs <= 0) return READY

	const startedAt = Date.parse(drawnAt)
	if (Number.isNaN(startedAt)) return READY

	const elapsed = now - startedAt
	if (elapsed >= cooldownMs) return READY

	// Far in the future means the stored instant is unusable - a clock change,
	// or a device that travelled. Never lock on it.
	if (elapsed < -cooldownMs) return READY

	const settled = Math.max(0, elapsed)

	return {
		isReady: false,
		remainingMs: cooldownMs - settled,
		ratio: settled / cooldownMs,
	}
}

/**
 * Formats what is left of the cooldown as a clock.
 *
 * @param remainingMs - Milliseconds left, as reported by
 *   {@link computeCooldown}.
 * @returns A `m:ss` string, rounded up so it never shows `0:00` while
 *   still locked.
 *
 * @example
 * formatRemaining(15200) // => "0:16"
 */
export function formatRemaining(remainingMs: number): string {
	const totalSeconds = Math.max(0, Math.ceil(remainingMs / 1000))
	const minutes = Math.floor(totalSeconds / 60)
	const seconds = totalSeconds % 60

	return `${String(minutes)}:${String(seconds).padStart(2, "0")}`
}
